import http from '../services/httpClient';
import { postUndo } from './ccsUndo';

/**
 * Every call the banquet lane makes, in one place.
 *
 * WHY THE ERRORS ARE RESHAPED HERE
 * --------------------------------
 * httpClient throws an Error carrying `.status` and `.data` for anything from
 * 400 up — except a 403, which it swallows and returns as a plain object. The
 * banquet screens need three specific outcomes (a coded field error, a named
 * duplicate, a closed revert window) and none of them should have to know that.
 * So every function here resolves to a uniform
 *
 *     { ok: true, ... }  |  { ok: false, code, error, ... }
 *
 * and no caller writes a try/catch or reads `err.data.code`. The backend
 * deliberately answers 409 rather than 403 for a closed revert window for the
 * same reason — a 403 would arrive with its `code` stripped.
 */

/** Pull a usable shape out of whatever httpClient threw. */
const asFailure = (err, fallback) => {
  const data = err?.data || {};
  return {
    ok: false,
    status: err?.status || 0,
    code: data.code || 'REQUEST_FAILED',
    error: data.error || err?.message || fallback,
    field: data.field || null,
    existing: data.existing ?? null,
    data: data.data ?? null,
    shortages: Array.isArray(data.shortages) ? data.shortages : null,
    mismatches: Array.isArray(data.mismatches) ? data.mismatches : null,
    reconciledThrough: data.reconciledThrough || null,
  };
};

/**
 * Record one banquet.
 *
 * `items` carries menuItemId and quantity only. totalBill and quantitySource
 * are the server's to decide and are not sent — see useBanquetEntry.
 * `allowNegativeStock` is sent true ONLY after the owner pressed "Confirm &
 * Create Negative Stock", exactly as the TSR page does.
 */
export async function submitBanquet({ eventDate, functionName, platesOrdered, ratePerPlate, items, notInMenuItems = [], notes, allowNegativeStock = false }) {
  try {
    const res = await http.post('/banquet/submit', {
      eventDate, functionName, platesOrdered, ratePerPlate, items, notInMenuItems, notes,
      allowNegativeStock: allowNegativeStock === true,
    });
    return { ok: true, ...res };
  } catch (err) {
    return asFailure(err, 'Could not record this banquet.');
  }
}

/**
 * Remove a banquet so it can be re-entered. There is no edit in v1.
 *
 * A banquet with wastage recorded against it reverts WITH that wastage, but
 * only once the owner has confirmed the exact list they were shown
 * (fetchRevertPreview): `includeWastage` true plus those `expectedWastageIds`.
 * The server compares them with what is actually linked and answers
 * BANQUET_WASTAGE_CHANGED if the list moved in the meantime.
 */
export async function revertBanquet(eventId, { includeWastage = false, expectedWastageIds = [] } = {}) {
  const body = includeWastage === true ? { includeWastage: true, expectedWastageIds } : {};
  try {
    const res = await postUndo(`/banquet/revert/${eventId}`, body || {});
    return { ok: true, ...res };
  } catch (err) {
    return asFailure(err, 'Could not revert this banquet.');
  }
}

/**
 * What reverting a banquet would do: the event, its own verdict, and every
 * wastage entry linked to it with that entry's verdict. Asked BEFORE the owner
 * confirms, so the popup can name the wastage that goes with the event.
 */
export async function fetchRevertPreview(eventId) {
  try {
    const res = await http.get(`/banquet/revert-preview/${eventId}`);
    return {
      ok: true,
      event: res.event || null,
      verdict: res.verdict || null,
      wastage: res.wastage || [],
      wastageTotal: Number(res.wastageTotal) || 0,
    };
  } catch (err) {
    return asFailure(err, 'Could not load this banquet.');
  }
}

/**
 * The wastage screen's "Banquet event" dropdown: functions held from date−3 to
 * date, after the counted period.
 */
export async function fetchWastageEvents(date) {
  try {
    const res = await http.get(`/banquet/wastage-events?date=${encodeURIComponent(date)}`);
    return {
      ok: true,
      events: res.events || [],
      range: res.range || null,
      reconciledThrough: res.reconciled_through || null,
    };
  } catch (err) {
    return asFailure(err, 'Could not load the banquets.');
  }
}

/** What may still be reverted, plus the boundary that decides it. */
export async function fetchBanquetRecent() {
  try {
    const res = await http.get('/banquet/recent-list');
    return { ok: true, events: res.events || [], window: res.window || null };
  } catch (err) {
    return asFailure(err, 'Could not load recent banquets.');
  }
}

/**
 * The report for a date range.
 * @param {string[]|number[]} [eventIds] chips the user has selected; empty means All.
 */
export async function fetchBanquetReport({ from, to, eventIds }) {
  const params = new URLSearchParams({ from, to });
  if (Array.isArray(eventIds) && eventIds.length > 0) params.set('eventIds', eventIds.join(','));
  try {
    const res = await http.get(`/banquet/report?${params.toString()}`);
    return { ok: true, ...res };
  } catch (err) {
    return asFailure(err, 'Could not load the banquet report.');
  }
}

/** The report's scope as a query string: the range, plus the chips when any are selected. */
const scopeParams = ({ from, to, eventIds }) => {
  const params = new URLSearchParams({ from, to });
  if (Array.isArray(eventIds) && eventIds.length > 0) params.set('eventIds', eventIds.join(','));
  return params.toString();
};

/**
 * Every function in the report's scope, with its dishes — the Functions list
 * behind the Billed card, and the rows of the export.
 */
export async function fetchBanquetEvents({ from, to, eventIds }) {
  try {
    const res = await http.get(`/banquet/report/events?${scopeParams({ from, to, eventIds })}`);
    return { ok: true, events: res.events || [], truncated: Boolean(res.truncated) };
  } catch (err) {
    return asFailure(err, 'Could not load the functions.');
  }
}

/**
 * One dish drilled into: the functions it went to, and each ingredient deducted
 * for it with its quantity and cost. The ingredient costs add up to
 * `ingredientsTotal.cost`, the dish's food cost.
 */
export async function fetchBanquetDishDetail({ from, to, eventIds, menuItemId }) {
  try {
    const res = await http.get(`/banquet/report/dish/${menuItemId}?${scopeParams({ from, to, eventIds })}`);
    return {
      ok: true,
      functions: res.functions || [],
      functionCount: Number(res.functionCount) || 0,
      functionsTruncated: Boolean(res.functionsTruncated),
      untrackedFunctions: Number(res.untrackedFunctions) || 0,
      ingredients: res.ingredients || [],
      ingredientsTracked: Boolean(res.ingredientsTracked),
      ingredientsTotal: res.ingredientsTotal || null,
      untracedCost: Number(res.untracedCost) || 0,
      costBasis: res.costBasis || null,
      sharedFunctions: Number(res.sharedFunctions) || 0,
    };
  } catch (err) {
    return asFailure(err, 'Could not load this dish.');
  }
}

/**
 * The Banquet End-of-Month report for one month ('YYYY-MM'). Banquet records
 * only — it shares nothing with the restaurant's EOM reports.
 */
export async function fetchBanquetEom(month) {
  try {
    const res = await http.get(`/banquet/eom?month=${encodeURIComponent(month)}`);
    return { ok: true, data: res.data || null };
  } catch (err) {
    return asFailure(err, 'Could not load the banquet end-of-month report.');
  }
}

/**
 * Recent function names matching what has been typed.
 *
 * Resolves to a plain array: a failed suggestion lookup must never interrupt
 * typing, so there is nothing for a caller to handle.
 */
export async function fetchFunctionSuggestions(q) {
  if (!String(q || '').trim()) return [];
  try {
    const res = await http.get(`/banquet/function-suggestions?q=${encodeURIComponent(q)}`);
    return res.suggestions || [];
  } catch {
    return [];
  }
}

export default {
  submitBanquet,
  revertBanquet,
  fetchRevertPreview,
  fetchWastageEvents,
  fetchBanquetRecent,
  fetchBanquetReport,
  fetchBanquetEvents,
  fetchBanquetDishDetail,
  fetchBanquetEom,
  fetchFunctionSuggestions,
};
